import {isDefined, isFalse} from "../../libs/utils/epic.utils";

/**
 * Die Offline-Fassung des iron-backup-viewers (scripts/viewer-offline.mjs) traegt alles in ihrer einen html: was die Seite
 * sonst per HTTP holt, als JSON-Block, dazu Worker und WASM als Base64. Auf der Webseite fehlen diese Bloecke.
 */
export const OFFLINE_DATA_ID: string = 'iron-backup-viewer-offline-data';
export const OFFLINE_WORKER_ID: string = 'iron-backup-viewer-offline-worker';
export const OFFLINE_WASM_ID: string = 'iron-backup-viewer-offline-wasm';
export const OFFLINE_HINT_ID: string = 'iron-backup-viewer-offline-hint';

/** Liegt neben der index.html jeder Fassung (scripts/viewer-offline.mjs); gespeichert wird sie unter offlineDownloadName(). */
export const OFFLINE_FILE: string = 'iron-backup-viewer-offline.html';

/** `viewer` ist das viewer.json des Baus, `files` alle uebrigen Dateien als Text, nach ihrem Pfad neben der index.html. */
export interface ViewerOfflineData {
  viewer: Record<string, unknown>;
  files: Record<string, string>;
}

const state: {
  read: boolean,
  data: ViewerOfflineData | undefined,
  worker: Uint8Array<ArrayBuffer> | undefined,
  wasm: Uint8Array<ArrayBuffer> | undefined
} = {read: false, data: undefined, worker: undefined, wasm: undefined};
const fileUrls: Map<string, string> = new Map<string, string>();

export function readOfflineData(doc: Document): ViewerOfflineData | undefined {
  const block: HTMLElement | null = doc.getElementById(OFFLINE_DATA_ID);
  return isDefined(block) ? JSON.parse(block.textContent ?? '') as ViewerOfflineData : undefined;
}

/** Die eingebetteten Daten, wenn diese Seite die Offline-Fassung ist - einmal gelesen, der Block aendert sich nicht. */
export function viewerOffline(): ViewerOfflineData | undefined {
  if (isFalse(state.read)) {
    state.data = readOfflineData(document);
    state.read = true;
  }
  return state.data;
}

export function isViewerOffline(): boolean {
  return isDefined(viewerOffline());
}

/**
 * Der Pfad, aus dem buildViewerInfo() bei ACCOUNTING die Stufe liest. Die Offline-Fassung liegt unter einem lokalen Pfad,
 * ihre Stufe steht im Bau: TEST baut `develop` (viewer.json, source.ref).
 */
export function viewerPathname(): string {
  const source: unknown = viewerOffline()?.viewer['source'];
  if (isViewerOffline()) {
    return typeof source === 'object' && isDefined(source) && (source as Record<string, unknown>)['ref'] === 'develop' ? '/test/' : '/staging/';
  }
  return window.location.pathname;
}

/** Unter diesem Namen speichert der Browser die Offline-Fassung - die Fassung im Namen ordnet sie der Sicherung zu. */
export function offlineDownloadName(version: string): string {
  return `iron-backup-viewer-${version}.html`;
}

/** Eine Datei neben dem iron-backup-viewer, etwa die Lizenzhinweise: online ihr Pfad, offline der eingebettete Text als blob:. */
export function offlineFileUrl(path: string): string {
  const text: string | undefined = viewerOffline()?.files[path];
  if (isDefined(text)) {
    const url: string = fileUrls.get(path) ?? URL.createObjectURL(new Blob([text], {type: 'text/plain;charset=utf-8'}));
    fileUrls.set(path, url);
    return url;
  }
  return path;
}

/** Der Code des DuckDB-Workers aus der Datei, einmal gelesen. */
export function offlineWorkerCode(): Uint8Array<ArrayBuffer> {
  state.worker ??= takeBytes(OFFLINE_WORKER_ID);
  return state.worker;
}

/** Das WASM bleibt gzip-komprimiert im Speicher (7,7 statt 34,3 MB) und wird je Engine neu entpackt, in 70 bis 190 ms. */
export async function offlineWasm(): Promise<Uint8Array<ArrayBuffer>> {
  state.wasm ??= takeBytes(OFFLINE_WASM_ID);
  const unpacked: ArrayBuffer = await new Response(new Blob([state.wasm]).stream().pipeThrough(new DecompressionStream('gzip'))).arrayBuffer();
  return new Uint8Array(unpacked);
}

// Danach verlaesst der Block das Dokument: Statt rund 11 MB Base64-Text bleiben nur die Bytes.
function takeBytes(id: string): Uint8Array<ArrayBuffer> {
  const block: HTMLElement | null = document.getElementById(id);
  const binary: string = atob(block?.textContent?.trim() ?? '');
  const bytes: Uint8Array<ArrayBuffer> = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  if (isDefined(block)) {
    block.remove();
  }
  return bytes;
}
